import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid"; // grid v2
import { useTheme } from "@mui/material/styles";
import { DateToBr, DateTimeToBr } from "../../../hooks/Utils";
import { useAuth } from "../../../contexts/AuthContext";
import getApiInstance from "../../../services/api";
import DateRangeSelector from "../../../components/ui/DateRangeSelector";
import { format, subDays } from "date-fns";

const SisfunerPayment = () => {
  const { system, parameters } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Detecta se é mobile/tablet

  // const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [insurances, setInsurances] = useState([]);
  const [orders, setOrders] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDetailsDialogOpen, setPaymentDetailsDialogOpen] =
    useState(false);
  const itensEncontradosRef = useRef(null);

  // Payment details state
  const [paymentMethod, setPaymentMethod] = useState("1"); // 1=débito, 2=crédito, 3=pix
  const [creditInstallments, setCreditInstallments] = useState(1); // 1 a 6 parcelas

  let intervalId;
  // SearchForm state
  const [searchForm, setSearchForm] = useState({
    dateRange: {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
    },
    osNumber: "",
    deceasedName: "",
    customerName: "",
    insuranceCompanyCode: null,
    onlyOpen: true,
  });

  // Carregar a lista de seguradoras ao iniciar
  useEffect(() => {
    const fetchInsurances = async () => {
      try {
        // setLoading(true);
        const api = getApiInstance(system);
        const response = await api.get("/data/insurances");
        setInsurances(response.data);
      } catch (err) {
        setError("Erro ao carregar lista de seguradoras");
        console.error(err);
      } finally {
        // setLoading(false);
      }
    };

    fetchInsurances();
  }, [system]);

  // Função para buscar ordens
  const handleSearch = async () => {
    try {
      setSearchLoading(true);
      setError("");
      setSuccess("");
      setOrders([]);
      setInstallments([]);
      setSelectedOrder(null);

      const api = getApiInstance(system);
      const payload = {
        dateRange: {
          startDate:
            !searchForm.osNumber &&
            format(searchForm.dateRange.startDate, "yyyy-MM-dd"),
          endDate:
            !searchForm.osNumber &&
            format(searchForm.dateRange.endDate, "yyyy-MM-dd"),
        },
        osNumber: searchForm.osNumber,
        deceasedName: searchForm.deceasedName,
        customerName: searchForm.customerName,
        insuranceCompanyCode: searchForm.insuranceCompanyCode?.code || "",
        onlyOpen: searchForm.onlyOpen,
      };

      const response = await api.post("/orders", payload);
      setOrders(response.data);
      setTimeout(() => {
        window.scrollTo({
          top: itensEncontradosRef?.current?.offsetTop,
          behavior: "smooth",
        });
      }, 300);

      if (response.data.length === 0) {
        setSuccess("Nenhum registro encontrado para os critérios informados.");
      }
    } catch (err) {
      setError(
        "Erro ao buscar registros: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para selecionar uma ordem e buscar suas parcelas
  const handleOrderSelect = async (order) => {
    try {
      setSearchLoading(true);
      setError("");
      setSelectedInstallments([]);

      if (!order) {
        setSelectedOrder(null);
        return;
      }

      const api = getApiInstance(system);
      const response = await api.get(
        `/orders/installments/${order.NumOrdem}/${order.Funeraria}`
      );

      setInstallments(response.data);
      setSelectedOrder(order);
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 300);
    } catch (err) {
      setError(
        "Erro ao buscar parcelas: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para selecionar/desselecionar parcelas
  const toggleInstallmentSelection = (installment) => {
    // Verificar se a parcela já está paga
    if (installment.DataBaixa !== null) return;

    setSelectedInstallments((prev) => {
      const isSelected = prev.some((item) => item.Seq === installment.Seq);

      if (isSelected) {
        return prev.filter((item) => item.Seq !== installment.Seq);
      } else {
        return [...prev, installment];
      }
    });
  };

  // Função para calcular o valor total selecionado
  const calculateTotalAmount = () => {
    return selectedInstallments.reduce((total, installment) => {
      return total + parseFloat(installment.Valor);
    }, 0);
  };

  // Função para abrir o diálogo de detalhes do pagamento
  const openPaymentDetailsDialog = () => {
    if (selectedInstallments.length === 0) {
      setError("Selecione pelo menos uma parcela para pagamento");
      return;
    }
    setPaymentDetailsDialogOpen(true);
  };

  // Função para processar o pagamento
  const handlePayment = async () => {
    try {
      setSearchLoading(true);
      setError("");
      setPaymentDetailsDialogOpen(false);

      const api = getApiInstance(system);
      const payload = {
        numOrdem: selectedOrder.NumOrdem,
        funeraria: selectedOrder.Funeraria,
        qtParcelas: paymentMethod === "2" ? creditInstallments : 1,
        seq: selectedInstallments
          .map((installment) => installment.Seq)
          .join(","),
        forma: parseInt(paymentMethod),
      };
      setDialogOpen(true);

      const response = await api.post("/payment/request", payload);

      if (!response.data.ok) {
        clearInterval(intervalId);
        setDialogOpen(false);
        setError(response.data.message || "Erro ao processar pagamento");
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }, 300);
        return;
      }

      setTransaction(response.data.posTransaction);

      // Iniciar verificação de status
      checkPaymentStatus(response.data.posTransaction.id);
    } catch (err) {
      clearInterval(intervalId);
      setError(
        "Erro ao processar pagamento: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para verificar status do pagamento
  const checkPaymentStatus = async (transactionId) => {
    try {
      setStatusLoading(true);
      const api = getApiInstance(system);

      // Loop de verificação a cada 3 segundos
      intervalId = setInterval(async () => {
        try {
          const response = await api.get(`/payment/status/${transactionId}`);

          // console.log(response.data);
          if (response.data.status === "cancelada") {
            clearInterval(intervalId);
            setPaymentResult(null);
            setStatusLoading(false);
          }
          if (response.data.status.Pagamentos) {
            clearInterval(intervalId);
            setPaymentResult(response.data.status.Pagamentos);
            handleOrderSelect(selectedOrder);
            setStatusLoading(false);
          }
        } catch (err) {
          console.error("Erro ao verificar status:", err);
        }
      }, 3000);

      // Timeout para evitar loop infinito (5 minutos)
      setTimeout(() => {
        clearInterval(intervalId);
        setStatusLoading(false);
        if (paymentResult) {
          setError("Tempo limite excedido para verificação do pagamento");
        }
      }, 300000);

      // Limpar o intervalo quando o componente for desmontado
      return () => clearInterval(intervalId);
    } catch (err) {
      setStatusLoading(false);
      setError(
        "Erro ao verificar status do pagamento: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    }
  };

  // Função para cancelar o pagamento
  const handleCancelPayment = async () => {
    try {
      if (!transaction) return;

      setTransaction(null);

      const api = getApiInstance(system);
      await api.delete(`/payment/cancelRequest/${transaction.id}`);

      clearInterval(intervalId);
      setDialogOpen(false);
      setPaymentResult(null);
    } catch (err) {
      clearInterval(intervalId);
      setError(
        "Erro ao cancelar pagamento: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    }
  };

  // Função para fechar o diálogo
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTransaction(null);
    setPaymentResult(null);
    setSelectedInstallments([]);
  };

  const renderOrderCard = (order) => {
    return !selectedOrder || selectedOrder.NumOrdem === order.NumOrdem ? (
      <Card
        key={`${order.NumOrdem}-${order.Funeraria}`}
        sx={{
          mb: 2,
          border:
            selectedOrder && selectedOrder.NumOrdem === order.NumOrdem
              ? `2px solid ${theme.palette.primary.main}`
              : "none",
        }}
      >
        <CardContent>
          <List disablePadding>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <ListItem>
                  <ListItemText
                    primary="Data"
                    secondary={DateTimeToBr(order.Data)}
                  />
                </ListItem>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <ListItem>
                  <ListItemText
                    primary="Nº Pedido"
                    secondary={order.NumOrdem}
                  />
                </ListItem>
              </Grid>
            </Grid>
            <ListItem>
              <ListItemText
                primary="Funerária"
                secondary={order.NomeFuneraria}
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Falecido" secondary={order.Falecido} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Cliente" secondary={order.Cliente} />
            </ListItem>
            {order.ResponsavelPagamento && (
              <ListItem>
                <ListItemText
                  primary="Resp. Pagamento"
                  secondary={order.ResponsavelPagamento}
                />
              </ListItem>
            )}
            {order.NomeSeguradora && (
              <ListItem>
                <ListItemText
                  primary="Seguradora"
                  secondary={order.NomeSeguradora}
                />
              </ListItem>
            )}
          </List>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            {!selectedOrder ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleOrderSelect(order)}
              >
                Ver Parcelas
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={() => handleOrderSelect(null)}
              >
                X
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    ) : null;
  };

  // Renderização de ordens em formato de cards (para telas pequenas e médias)
  const renderOrderCards = () => {
    return orders.map((order) => renderOrderCard(order));
  };

  // Renderização de parcelas em formato de cards (para telas pequenas e médias)
  const renderInstallmentCards = () => {
    return installments
      .filter(
        (installment) =>
          !searchForm.onlyOpen ||
          (searchForm.onlyOpen && !installment.DataBaixa)
      )
      .map((installment) => (
        <Card
          key={installment.Seq}
          sx={{
            mb: 2,
            border: selectedInstallments.some(
              (item) => item.Seq === installment.Seq
            )
              ? `2px solid ${theme.palette.primary.main}`
              : "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleInstallmentSelection(installment);
          }}
        >
          <CardContent>
            {/* <List>
              <ListItem>
                <pre>
                  {JSON.stringify({ searchForm, installment }, null, 2)}
                </pre>
              </ListItem>
            </List> */}
            <List disablePadding>
              <ListItem>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedInstallments.some(
                        (item) => item.Seq === installment.Seq
                      )}
                      onChange={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleInstallmentSelection(installment);
                      }}
                      disabled={installment.DataBaixa !== null}
                    />
                  }
                  label={`Selecionar Ref: ${installment.Seq}`}
                />
              </ListItem>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <ListItem>
                    <ListItemText
                      primary="Vencimento"
                      secondary={DateToBr(installment.Vencimento)}
                      sx={{ flex: "0 0 130px" }}
                    />
                  </ListItem>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <ListItem>
                    <ListItemText
                      primary="Valor"
                      secondary={parseFloat(installment.Valor).toLocaleString(
                        "pt-BR",
                        {
                          style: "currency",
                          currency: "BRL",
                        }
                      )}
                      primaryTypographyProps={{ variant: "subtitle2" }}
                      sx={{ flex: "0 0 130px" }}
                    />
                  </ListItem>
                </Grid>

                {installment.DataBaixa && (
                  <Grid size={{ xs: 6 }}>
                    <ListItem>
                      <ListItemText
                        primary="Data Baixa"
                        secondary={
                          (installment.DataBaixa &&
                            DateTimeToBr(installment.DataBaixa)) ||
                          "-"
                        }
                        primaryTypographyProps={{ variant: "subtitle2" }}
                        sx={{ flex: "0 0 130px" }}
                      />
                    </ListItem>
                  </Grid>
                )}

                {installment.ValorBaixa && (
                  <Grid size={{ xs: 6 }}>
                    <ListItem>
                      <ListItemText
                        primary="Valor Baixa"
                        secondary={
                          installment.ValorBaixa
                            ? parseFloat(installment.ValorBaixa).toLocaleString(
                                "pt-BR",
                                {
                                  style: "currency",
                                  currency: "BRL",
                                }
                              )
                            : "-"
                        }
                        primaryTypographyProps={{ variant: "subtitle2" }}
                        sx={{ flex: "0 0 130px" }}
                      />
                    </ListItem>
                  </Grid>
                )}
              </Grid>
            </List>
          </CardContent>
        </Card>
      ));
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Pagamento
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, maxWidth: 900, mx: "auto" }}>
        <Typography variant="h6" gutterBottom>
          Filtros de Busca
        </Typography>
        <Grid container spacing={2}>
          {!searchForm.osNumber && (
            <Grid size={{ xs: 12 }}>
              <DateRangeSelector
                startDate={searchForm.dateRange.startDate}
                endDate={searchForm.dateRange.endDate}
                onChange={(dateRange) =>
                  setSearchForm((prev) => ({ ...prev, dateRange }))
                }
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Número da OS"
              value={searchForm.osNumber}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  osNumber: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Nome do Falecido"
              value={searchForm.deceasedName}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  deceasedName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Nome do Cliente"
              value={searchForm.customerName}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  customerName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ alignItems: "center" }}>
            <Autocomplete
              options={insurances}
              getOptionLabel={(option) => option.name}
              value={searchForm.insuranceCompanyCode}
              onChange={(_, newValue) => {
                setSearchForm((prev) => ({
                  ...prev,
                  insuranceCompanyCode: newValue,
                }));
              }}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Seguradora" fullWidth />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchForm.onlyOpen}
                  onChange={(e) =>
                    setSearchForm((prev) => ({
                      ...prev,
                      onlyOpen: e.target.checked,
                    }))
                  }
                />
              }
              label="Apenas Pendentes"
            />
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? "Carregando..." : "LISTAR"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {orders.length > 20 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Exibindo os primeiros ${orders.length} resultados - refine sua busca`}
        </Alert>
      )}

      {orders.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom ref={itensEncontradosRef}>
            Itens Encontrados
          </Typography>

          {/* Exibição para desktop (tabela) */}
          {!isMobile && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Nº Pedido</TableCell>
                    <TableCell>Funerária</TableCell>
                    <TableCell>Falecido</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Resp. Pagamento</TableCell>
                    <TableCell>Seguradora</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={`${order.NumOrdem}-${order.Funeraria}`}
                      selected={
                        selectedOrder &&
                        selectedOrder.NumOrdem === order.NumOrdem
                      }
                    >
                      <TableCell>{DateTimeToBr(order.Data)}</TableCell>
                      <TableCell>{order.NumOrdem}</TableCell>
                      <TableCell>{order.NomeFuneraria}</TableCell>
                      <TableCell>{order.Falecido}</TableCell>
                      <TableCell>{order.Cliente}</TableCell>
                      <TableCell>{order.ResponsavelPagamento}</TableCell>
                      <TableCell>{order.NomeSeguradora}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleOrderSelect(order)}
                        >
                          Ver Parcelas
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Exibição para mobile e tablet (cards) */}
          {isMobile && renderOrderCards()}
        </Paper>
      )}

      {selectedOrder && installments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Parcelas de {selectedOrder.NumOrdem}
          </Typography>

          {/* Exibição para desktop (tabela) */}
          {!isMobile && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Seq</TableCell>
                    <TableCell>Lançamento</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Data Baixa</TableCell>
                    <TableCell align="right">Valor Baixa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installments
                    .filter(
                      (installment) =>
                        !searchForm.onlyOpen || !installment.DataBaixa
                    )
                    .map((installment) => (
                      <TableRow
                        key={installment.Seq}
                        selected={selectedInstallments.some(
                          (item) => item.Seq === installment.Seq
                        )}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedInstallments.some(
                              (item) => item.Seq === installment.Seq
                            )}
                            onChange={() =>
                              toggleInstallmentSelection(installment)
                            }
                            disabled={installment.DataBaixa !== null}
                          />
                        </TableCell>
                        <TableCell>{installment.Seq}</TableCell>
                        <TableCell>{installment.Lancamento}</TableCell>
                        <TableCell>
                          {DateToBr(installment.Vencimento)}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(installment.Valor).toLocaleString(
                            "pt-BR",
                            {
                              style: "currency",
                              currency: "BRL",
                            }
                          )}
                        </TableCell>
                        <TableCell>
                          {(installment.DataBaixa &&
                            DateTimeToBr(installment.DataBaixa)) ||
                            "-"}
                        </TableCell>
                        <TableCell align="right">
                          {installment.ValorBaixa
                            ? parseFloat(installment.ValorBaixa).toLocaleString(
                                "pt-BR",
                                {
                                  style: "currency",
                                  currency: "BRL",
                                }
                              )
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Exibição para mobile e tablet (cards) */}
          {isMobile && renderInstallmentCards()}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedInstallments.length === 0 || searchLoading}
              onClick={openPaymentDetailsDialog}
            >
              Efetuar Pagamento
            </Button>
          </Box>
        </Paper>
      )}

      {/* Diálogo de detalhes do pagamento */}
      <Dialog
        open={paymentDetailsDialogOpen}
        onClose={() => setPaymentDetailsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
        >
          Detalhes do Pagamento
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ md: 6 }}>
                <FormControl>
                  <FormLabel>O.S.</FormLabel>
                  <Typography variant="h5" gutterBottom>
                    {selectedOrder ? selectedOrder.NumOrdem : "Nenhuma seleção"}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid size={{ md: 6 }}>
                <FormControl size={{ md: 6 }}>
                  <FormLabel>Nº Referência</FormLabel>
                  <Typography variant="h7" gutterBottom>
                    [{selectedInstallments.map((i) => i.Seq).join(" / ")}]
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h5" color="primary" gutterBottom>
              Valor Total:{" "}
              {calculateTotalAmount().toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            </Typography>

            <FormControl component="fieldset" sx={{ mt: 3 }}>
              <FormLabel component="legend">Forma de Pagamento</FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label="Débito"
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label="Crédito"
                />
                <FormControlLabel value="4" control={<Radio />} label="PIX" />
              </RadioGroup>
            </FormControl>

            {paymentMethod === "2" && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <Select
                  value={creditInstallments}
                  onChange={(e) => setCreditInstallments(e.target.value)}
                  label="Quantidade de Parcelas"
                >
                  {Array.from(
                    { length: parameters?.maxInstallments || 6 },
                    (_, i) => i + 1
                  ).map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}x {num > 1 ? "parcelas" : "parcela"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPaymentDetailsDialogOpen(false)}
            color="secondary"
          >
            Cancelar
          </Button>
          <Button onClick={handlePayment} color="primary" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de acompanhamento do pagamento */}
      <Dialog open={dialogOpen} fullWidth maxWidth="md">
        <DialogTitle>Processando Pagamento</DialogTitle>
        <DialogContent>
          {statusLoading && !paymentResult && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 3,
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                Aguardando confirmação do pagamento...
              </Typography>
            </Box>
          )}

          {paymentResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Pagamento Concluído
              </Typography>

              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(paymentResult.pagamento, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!paymentResult && transaction && (
            <Button onClick={handleCancelPayment} color="error">
              Cancelar
            </Button>
          )}
          {paymentResult && (
            <Button onClick={handleCloseDialog} color="primary">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SisfunerPayment;
